import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  ArrowRight, Cpu, Receipt, Store, UtensilsCrossed, Sparkles, Shirt, ShieldCheck, Wifi, BarChart3, KeyRound,
  Pizza, Coffee, IceCream, Croissant, Cigarette, Newspaper, Scissors, ShoppingCart, Beef, Wine, Car, BedDouble, Dog
} from "lucide-react";

const SCENES = [
  { src: "/landing-toelettatura.jpg", label: "Toelettatura" },
  { src: "/landing-ristorante.jpg", label: "Ristorante" },
  { src: "/landing-cassa.jpg", label: "Cassa" },
];

const MODE_ICONS = {
  negozio_animali: Store, toelettatura: Dog,
  ristorante: UtensilsCrossed, festa: Sparkles, negozio: Store, lavanderia: Shirt,
  pizzeria: Pizza, bar: Coffee, gelateria: IceCream, panetteria: Croissant,
  tabacchi: Cigarette, edicola: Newspaper, parrucchiere: Scissors,
  alimentari: ShoppingCart, macelleria: Beef, enoteca: Wine, autolavaggio: Car, bnb: BedDouble,
};

const cardLift = {
  boxShadow:
    "8px 12px 28px rgba(24, 24, 27, 0.06), -6px -8px 20px rgba(255, 255, 255, 0.85), inset 0 1px 0 rgba(255,255,255,0.9)",
};

const Logo = ({ light = false }) => (
  <div className="flex items-center gap-3 select-none" data-testid="velta-logo">
    <img src={light ? "/velta-one-mark-white.png" : "/velta-one-mark.png"} alt="Velta One" className="w-14 h-8 object-contain shrink-0" />
    <div className="leading-tight">
      <div className={`font-heading font-semibold tracking-tight text-xl ${light ? "text-white" : "text-[#18181B]"}`}>Velta One</div>
      <div className={`text-[8px] uppercase tracking-[0.3em] mt-0.5 ${light ? "text-white/40" : "text-[#A1A1AA]"}`}>by Velta Sistemi</div>
    </div>
  </div>
);

const ModeCard = ({ icon: Icon, title, desc, testid }) => (
  <div
    data-testid={testid}
    className="bg-white border border-[#E4E4E7]/80 p-7 rounded-2xl transition-colors group"
    style={cardLift}
  >
    <div className="w-11 h-11 bg-[#18181B] text-white flex items-center justify-center rounded-xl mb-5">
      <Icon className="w-5 h-5" strokeWidth={1.8} />
    </div>
    <h3 className="text-lg font-heading font-semibold mb-1.5 text-[#18181B]">{title}</h3>
    <p className="text-[#52525B] text-xs leading-relaxed">{desc}</p>
  </div>
);

export default function Landing() {
  const [modes, setModes] = useState([]);
  useEffect(() => { api.get("/modes").then(r => setModes(Array.isArray(r.data) ? r.data : [])); }, []);
  return (
    <div className="relative min-h-screen overflow-hidden bg-[#EFECE6] text-[#3F3F46]">

      <header className="relative z-30 sticky top-0 border-b border-[#E4E4E7]/80 bg-white/85 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Logo />
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
            <a href="#modalita" className="text-[#52525B] hover:text-[#18181B]">Modalità</a>
            <a href="#features" className="text-[#52525B] hover:text-[#18181B]">Funzionalità</a>
            <a href="#contatti" className="text-[#52525B] hover:text-[#18181B]">Contatti</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="header-login-link" className="text-sm font-medium text-[#52525B] hover:text-[#18181B]">Accedi</Link>
            <Link
              to="/preventivo"
              data-testid="header-activate-btn"
              className="hidden sm:inline-flex items-center gap-2 bg-[#B73C27] hover:bg-[#98301F] text-white px-4 h-10 rounded-xl text-sm font-medium transition-colors btn-tactile"
              style={{ boxShadow: "0 8px 18px rgba(183, 60, 39, 0.22)" }}
            >
              Richiedi preventivo <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </header>

      <section className="relative z-10 overflow-hidden min-h-[560px]">
        <img src="/landing-cassa.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(100deg, rgba(247,245,241,0.96) 0%, rgba(247,245,241,0.9) 46%, rgba(247,245,241,0.45) 100%)" }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 animate-fade-in">
            <div className="inline-flex items-center gap-2 border border-[#E4E4E7] bg-white px-3 py-1.5 rounded-full text-[11px] uppercase tracking-[0.18em] font-medium text-[#71717A] mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-[#B73C27]" /> Made in Italy · RT certificate
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-[4.25rem] font-semibold leading-[1.05] tracking-tight text-[#18181B]">
              Il punto cassa fiscale che<br />
              lavora come te.
            </h1>
            <p className="mt-8 text-lg sm:text-xl text-[#52525B] max-w-2xl leading-relaxed">
              Software professionale per ristoranti, negozi, feste di paese e lavanderie. Collegamento TCP/IP diretto a stampanti fiscali RT, gestione multi-negozio, statistiche in tempo reale.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link
                to="/login"
                data-testid="hero-activate-btn"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap bg-[#B73C27] hover:bg-[#98301F] text-white px-7 h-14 text-base font-medium rounded-xl btn-tactile"
                style={{ boxShadow: "0 10px 22px rgba(183, 60, 39, 0.22)" }}
              >
                Accedi al gestionale <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/preventivo"
                data-testid="hero-quote-btn"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap bg-white border border-[#E4E4E7] text-[#18181B] hover:border-[#18181B] px-7 h-14 text-base font-medium rounded-xl btn-tactile"
              >
                Trova la cassa adatta alla tua attività
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5 hidden lg:block">
            <div className="bg-white border border-[#E4E4E7]/80 rounded-2xl p-8 font-mono text-sm" style={cardLift}>
              <div className="text-[#A1A1AA] mb-3 uppercase tracking-[0.2em] text-[11px] font-medium">Scontrino fiscale</div>
              <div className="border-t border-[#E4E4E7] pt-4 space-y-2 text-[#18181B]">
                <div className="flex justify-between"><span className="text-[#52525B]">Pizza Margherita</span><span>€ 8,00</span></div>
                <div className="flex justify-between"><span className="text-[#52525B]">Coca Cola</span><span>€ 3,50</span></div>
                <div className="flex justify-between"><span className="text-[#52525B]">Tiramisù</span><span>€ 6,00</span></div>
                <div className="border-t border-[#E4E4E7] my-3" />
                <div className="flex justify-between text-base font-semibold">
                  <span>TOTALE</span>
                  <span className="text-[#B73C27]">€ 17,50</span>
                </div>
                <div className="text-[#A1A1AA] text-xs mt-4">RT #000124 · IVA 22% scorporata · {new Date().toLocaleDateString("it-IT")}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative z-10 -mt-6 pb-2" aria-label="Attività">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid md:grid-cols-3 gap-3">
          {SCENES.map((scene) => (
            <div key={scene.label} className="relative h-44 sm:h-52 overflow-hidden rounded-2xl border border-white/70">
              <img src={scene.src} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#18181B]/70 via-[#18181B]/10 to-transparent" />
              <div className="absolute bottom-4 left-5 text-white font-heading text-xl font-semibold">{scene.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section id="modalita" className="relative z-10 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
            <div>
              <div className="text-[11px] font-medium uppercase tracking-[0.28em] text-[#A1A1AA] mb-3">01 — Modalità di lavoro</div>
              <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-[#18181B] max-w-2xl">{modes.length}+ modalità. Una sola piattaforma.</h2>
            </div>
            <p className="text-[#52525B] max-w-md leading-relaxed">Configura in fase di attivazione le modalità più adatte alla tua attività. Vendi di più? Aggiungi un altro modulo.</p>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {(Array.isArray(modes) ? modes : []).map(m => (
              <ModeCard key={m.id} testid={`mode-card-${m.id}`} icon={MODE_ICONS[m.id] || Store} title={m.label} desc={m.description} />
            ))}
          </div>
        </div>
      </section>

      <section id="features" className="relative z-10 py-20 lg:py-28 bg-[#F7F5F1]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-[11px] font-medium uppercase tracking-[0.28em] text-[#A1A1AA] mb-3">02 — Funzionalità</div>
          <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-[#18181B] max-w-3xl mb-14">Pensato per il fiscale italiano. Costruito per la velocità.</h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Receipt, t: "Stampanti fiscali RT", d: "Connessione TCP/IP diretta. Compatibilità Epson, RCH, Custom, Ditron." },
              { icon: Wifi, t: "Offline-first", d: "Lavora anche senza rete. Sincronizza automaticamente al ritorno della connessione." },
              { icon: KeyRound, t: "Lotteria scontrini", d: "Tasto dedicato su subtotale. Inserimento manuale o lettura barcode. Pronto per lotteria istantanea." },
              { icon: BarChart3, t: "Statistiche complete", d: "Per categoria, articolo, giorno, mese o data libera. Per metodo di pagamento. Grafici interattivi." },
              { icon: Cpu, t: "Multi-negozio", d: "Magazzino centrale con carico e scarico tra punti vendita. Vedi tutto dal portale remoto." },
              { icon: ShieldCheck, t: "Attivazione VELTA", d: "Il locale lo apriamo noi. Il titolare entra con email e password." },
            ].map((f) => (
              <div key={f.t} className="bg-white border border-[#E4E4E7]/80 rounded-2xl p-8" style={cardLift}>
                <f.icon className="w-7 h-7 mb-5 text-[#B73C27]" strokeWidth={1.8} />
                <h3 className="font-heading text-xl font-semibold mb-2 text-[#18181B]">{f.t}</h3>
                <p className="text-[#52525B] text-sm leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="contatti" className="relative z-10 py-16 lg:py-24 overflow-hidden">
        <img src="/landing-ristorante.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#EFECE6]/88" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-12">
          <div className="bg-white border border-[#E4E4E7]/80 rounded-2xl px-8 py-16 sm:px-12 text-center" style={cardLift}>
            <div className="text-[11px] font-medium uppercase tracking-[0.28em] text-[#A1A1AA] mb-4">03 — Inizia oggi</div>
            <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-[#18181B] mb-5">Vuoi attivare la tua attività?</h2>
            <p className="text-[#52525B] text-lg mb-10 max-w-xl mx-auto leading-relaxed">L&apos;attivazione è gestita da VELTA SISTEMI. Richiedi un preventivo o accedi con le credenziali del titolare.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/preventivo"
                data-testid="cta-activate-btn"
                className="inline-flex items-center justify-center gap-2 bg-[#B73C27] hover:bg-[#98301F] text-white px-8 h-14 text-base font-medium rounded-xl btn-tactile"
                style={{ boxShadow: "0 10px 22px rgba(183, 60, 39, 0.22)" }}
              >
                Richiedi preventivo <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/login"
                data-testid="cta-login-btn"
                className="inline-flex items-center justify-center gap-2 bg-white border border-[#E4E4E7] text-[#18181B] hover:border-[#18181B] px-8 h-14 text-base font-medium rounded-xl transition-colors btn-tactile"
              >
                Sono già cliente
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="relative z-10 border-t border-[#E4E4E7] bg-white/80">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <Logo />
          <div className="text-sm text-[#71717A]">© {new Date().getFullYear()} VELTA SISTEMI · Punto Cassa Fiscale</div>
        </div>
      </footer>
    </div>
  );
}
